import React, { useEffect, useState } from "react";
import "../../styles/ticket.scss";
import { tryFetch, IResponse } from "../../helpers/FetchAPI";
import { getTokenOrDefault } from "../../helpers/Storage";
import { Link } from "react-router-dom";
import TicketFilter from "./TicketFilter";

interface Order {
  id: string;
  order_id: string;
  event_title: string;
  event_image: string;
  selected_date: string;
  selected_time: string;
  event_venue: string;
  total_amount: number;
  quantity: number;
  ticket_url: string;
  selected_ticket_type: string;
  create_at: string;
  status: string;
  event_date_raw: string;
}

const TicketPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [timers, setTimers] = useState<{ [key: string]: string }>({});
  const [activeFilter, setActiveFilter] = useState("ALL");

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const token = getTokenOrDefault();
        const res: IResponse = await tryFetch({
          url: "/sec/ticket-orders",
          method: "GET",
          token,
        });

        if (res.is_success && Array.isArray(res.data)) {
          const mappedOrders: Order[] = (res.data as any[]).map((item) => {
            const eventDate = new Date(item.event_start_date);
            return {
              id: item.id,
              order_id: item.order_id,
              event_title: item.event_title,
              event_image: item.event_image,
              selected_date: eventDate.toLocaleDateString("en-PH", {
                month: "long",
                day: "numeric",
                year: "numeric",
              }),
              selected_time: eventDate.toLocaleTimeString("en-PH", {
                hour: "2-digit",
                minute: "2-digit",
              }),
              event_date_raw: item.event_start_date,
              event_venue: item.event_venue,
              total_amount: parseFloat(item.order_total || "0"),
              quantity: Number(item.ticket_count || 0),
              ticket_url: item.ticket_url,
              selected_ticket_type: item.ticket_type,
              create_at: item.created_at,
              status: item.status,
            };
          });

          setOrders(mappedOrders);
        } else {
          console.error("Failed to fetch tickets:", res.error);
        }
      } catch (err) {
        console.error("Error fetching tickets:", err);
      }
    };

    fetchTickets();
  }, []);


  const parseDate = (dateString: string) => {
    if (!dateString) return new Date();
    return new Date(dateString.replace(/-/g, "/"));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const newTimers: { [key: string]: string } = {};

      orders.forEach((order) => {
        if (order.status === "PENDING") {
          const createdAt = parseDate(order.create_at);
          const expireTime = createdAt.getTime() + 15 * 60 * 1000;
          const now = Date.now();
          const diff = expireTime - now;

          if (diff > 0) {
            const minutes = Math.floor(diff / 1000 / 60);
            const seconds = Math.floor((diff / 1000) % 60);
            newTimers[order.id] = `${minutes}:${seconds
              .toString()
              .padStart(2, "0")}`;
          } else {
            newTimers[order.id] = ""; 
          }
        }
      });

      setTimers(newTimers);
    }, 1000);

    return () => clearInterval(interval);
  }, [orders]);

  const filteredOrders = orders.filter((order) => {
    const today = new Date();
    const eventDate = new Date(order.event_date_raw);

    if (activeFilter === "TO PAY") {
      return order.status === "PENDING";
    }
    if (activeFilter === "PAID") {
      return order.status === "PAID" && eventDate >= today;
    }
    if (activeFilter === "COMPLETED") {
      return order.status === "PAID" && eventDate < today;
    }
    if (activeFilter === "CANCELLED") {
      return order.status === "CANCELLED";
    }
    return order.status === "PENDING" || order.status === "PAID";
  });

  return (
    <div className="ticket-page">
      <h2>My Tickets</h2>
      <hr className="ticket-divider" />

      <TicketFilter
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
      />

       {filteredOrders.length === 0 ? (
        <div className="no-tickets">
          <p>No tickets found.</p>
        </div>
      ) : (
        filteredOrders.map((order) => (
          <div className="ticket-card" key={order.id}>
            <div className="event-section">
              <img
                src={order.event_image}
                alt={order.event_title}
                className="event-img"
              />
              <div className="timer-container">
                {order.status === "PENDING" && timers[order.id] && (
                  <p className="timer">{timers[order.id]}</p>
                )}
              </div>
            </div>
            <div className="ticket-details">
              <small className="created-at">{order.create_at}</small>
              <br />
              <small className="order-id">{order.order_id}</small>
              <h3 className="event-title">{order.event_title}</h3>
              <p className="event-meta">{order.selected_date}</p>
              <p className="event-meta">{order.selected_time}</p>
              <p className="event-meta">{order.event_venue}</p>
              <hr />
              <p>
                <strong>Number of Pax:</strong> {order.quantity}
              </p>
              <p>
                <strong>Ticket Type:</strong> {order.selected_ticket_type}
              </p>
              <p>
                <strong>Total Amount:</strong> ₱
                {Number(order.total_amount || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </p>

              {order.status === "PENDING" && (
                <Link to={`/preview/${order.id}`} className="download-btn">
                  Pay Now
                </Link>
              )}
              {order.status === "PAID" && (
                <a
                  href={order.ticket_url}
                  className="download-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Ticket
                </a>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default TicketPage;
      

      {/* {filteredOrders.map((order) => (
        <div className="ticket-card" key={order.id}>
          <div className="event-section">
            <img
              src={order.event_image}
              alt={order.event_title}
              className="event-img"
            />
            <div className="timer-container">
              {order.status === "PENDING" && timers[order.id] && (
                <p className="timer">{timers[order.id]}</p>
              )}
            </div>
          </div>
          <div className="ticket-details">
            <small className="created-at">{order.create_at}</small>
            <br />
            <small className="order-id">{order.order_id}</small>
            <h3 className="event-title">{order.event_title}</h3>
            <p className="event-meta">{order.selected_date}</p>
            <p className="event-meta">{order.selected_time}</p>
            <p className="event-meta">{order.event_venue}</p>
            <hr />
            <p>
              <strong>Number of Pax:</strong> {order.quantity}
            </p>
            <p>
              <strong>Ticket Type:</strong> {order.selected_ticket_type}
            </p>
            <p>
              <strong>Total Amount:</strong> ₱
              {Number(order.total_amount || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </p>

            {order.status === "PENDING" && (
              <Link to={`/preview/${order.id}`} className="download-btn">
                Pay Now
              </Link>
            )}
            {order.status === "PAID" && (
              <a
                href={order.ticket_url}
                className="download-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Ticket
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TicketPage; */}